'use strict';

import PouchDB from 'pouchdb';
import moment from 'moment';
import log from '@/logger';

import { namespacedActions as companyFilesActions } from '@/store/companyFiles/actionTypes';

var state;
var dispatch;

var externalDocCount = 0;
var syncProgress = 0;

const backendDocTypes = ['ProjectManagerConfig', 'UserManagerConfig'];

const backOff = (delay) => (delay === 0 ? 500 : 1000);

const getSyncParams = (live, batchSize = 100) => ({
  pull: {
    live,
    batch_size: batchSize,
    retry: true,
    back_off_function: backOff,
    filter(doc) {
      return !backendDocTypes.includes(doc.type);
    },
  },
  push: {
    live,
    batch_size: batchSize,
    retry: true,
    back_off_function: backOff,
    filter(doc) {
      return !doc._id.startsWith('_design') && !backendDocTypes.includes(doc.type);
    },
  },
});

export const replicate = function (credentials, db, dbProps) {
  if (document.boonTesting) {
    log.debug('Skipping log sync because of test');
    return;
  }

  const url = new URL(dbProps.db, dbProps.baseUrl);
  url.username = credentials.username;
  log.debug('Syncer', ['Pushing changes downstream to', url.toString()]);
  url.password = credentials.password;
  PouchDB.replicate(db, url.toString(), {
    live: true,
    retry: true,
    batch_size: 1000,
  }).catch((e) => {
    log.error('Log Replicator', ['Error', e]);
    setTimeout(() => replicate(credentials, db, dbProps), 5000);
  });
};

export const initSync = (db, initState, initDispatch, masterdb, live = false) => {
  state = initState;
  dispatch = initDispatch;
  var startTime = moment();
  if (document.boonTesting) {
    log.debug('Skipping syncer because of test');
    dispatch('setSyncStatus', { status: 'Active', live: true, message: '', progress: syncProgress });
    return;
  }

  const url = new URL(masterdb.db, masterdb.baseUrl);
  url.username = masterdb.username;
  log.debug('Syncer', ['Initiating', url.toString()]);
  url.password = masterdb.password;
  const externalDb = new PouchDB(url.toString());
  if (live === false) {
    externalDb
      .info()
      .then((info) => {
        log.debug('Syncer', ['External db', masterdb.db, 'got', info.doc_count]);
        externalDocCount = info.doc_count;
      })
      .catch((e) => {
        log.error('Syncer', ['Error fetching doc count', masterdb.db, e]);
      });
  }

  return PouchDB.sync(masterdb.db, externalDb, getSyncParams(live))
    .on('change', function (info) {
      log.debug('Syncer', ['About to sync', info.change.docs.length, 'documents']);

      dispatch('setSyncStatus', { live, status: 'Active', message: 'Syncing: {progress}%', progress: syncProgress });

      if (live === false) {
        db.info()
          .then((info) => {
            log.debug('Syncer', ['Db got', info.doc_count]);

            if (info.doc_count < externalDocCount && externalDocCount > 0) {
              log.info('Syncer', ['Synced', info.doc_count / externalDocCount, '%']);
              syncProgress = Math.round((100 * info.doc_count) / externalDocCount);
              dispatch('setSyncStatus', {
                live,
                status: 'Active',
                message: 'Syncing: {progress}%',
                progress: syncProgress,
              });
            }
          })
          .catch((e) => {
            log.error('Syncer', ['Error fetching doc count', db, e]);
          });
        return;
      }

      for (var doc of info.change.docs) {
        log.debug('Syncer, ready to sync', doc);
        try {
          if (doc._deleted === true) {
          } else if (doc.type === 'Part') {
            syncPart(doc);
          } else if (doc.type === 'PartComment') {
            syncPartComment(doc);
          } else if (doc.type === 'Stage') {
            syncStage(doc);
          } else if (doc.type === 'Project') {
            syncProject(doc);
          } else if (doc.type === 'Company') {
            syncCompany(doc);
          } else if (doc.type === 'CompanyFiles') {
            syncCompanyFiles(doc);
          } else if (doc.type === 'Person') {
            syncPerson(doc);
          } else if (doc.type === 'dagbok') {
            syncDiary(doc);
          } else if (backendDocTypes.includes(doc.type) || doc.type === 'Log' || doc.type === 'WorkplaceIntroduction') {
            /** @todo WorkplaceIntroduction is legacy doc type and should be removed once it is no longer required. */
            log.debug('Syncer', ['ignoring ', doc.type, doc._id]);
          } else if (doc._id.startsWith('_design/')) {
            log.debug('Syncer', ['ignoring design doc', doc._id]);
          } else {
            dispatch('sync' + doc.type, doc);
          }
        } catch (e) {
          log.error('Syncer', ['Error syncing', doc.type, doc._id, e]);
        }
      }
      log.debug('Syncer, synced documents', { count: info.change.docs.length });
    })
    .on('paused', function (err) {
      if (err) {
        log.error('Syncer', ['sync paused due to error', err]);
        dispatch('setSyncStatus', { status: 'Error', live: live, message: err.message });
      } else {
        log.debug('Syncer', ['sync paused']);
        dispatch('setSyncStatus', { status: 'Paused', live: live, message: '' });
      }
    })
    .on('active', function () {
      log.debug('Syncer', ['sync active']);
      dispatch('setSyncStatus', { status: 'Active', live: live, message: '' });
    })
    .on('denied', function (err) {
      log.error('Syncer', ['sync denied due to error', err]);
      dispatch('setSyncStatus', { status: 'Error', live: live, message: err.message });
    })
    .on('complete', function (info) {
      log.info('Syncer', ['Initial sync done', moment().diff(startTime, 'second')]);
      dispatch('setSyncStatus', { status: 'Complete', live: live, message: '' });
      initSync(db, state, dispatch, masterdb, true);
    })
    .on('error', function (err) {
      log.error('Syncer', ['sync error', err]);
      dispatch('setSyncStatus', { status: 'Error', live: live, message: err.message });
    })
    .catch((err) => {
      log.error('Syncer', ['sync caught error', err]);
      dispatch('setSyncStatus', { status: 'Error', live: live, message: err.message });
    });
};

function syncPart(doc) {
  var found = state.parts[doc._id];

  if (!found && doc.projectId === state.currentProject._id) {
    dispatch('addSyncedPart', doc);
  } else if (found) {
    if (found._rev !== doc._rev) {
      dispatch('syncPartProperties', { updatePart: found, newPartProperties: doc });
    }
  }
}

function syncPartComment(doc) {
  dispatch('syncPartComment', doc);
}

function syncCompany(doc) {
  if (!state.companies) {
    return;
  }
  var found = state.companies[doc._id];

  if (!found) {
    dispatch('addSyncedCompany', doc);
  } else {
    delete doc._revisions;
    if (found._rev !== doc._rev) {
      dispatch('syncCompanyProperties', { updateCompany: found, newCompanyProperties: doc });
    }
  }
}

const syncCompanyFiles = (entity) => {
  dispatch(companyFilesActions.syncCompanyFiles, { entity });
};

function syncPerson(doc) {
  if (!state.persons) {
    return;
  }
  var found = state.persons[doc._id];

  if (!found) {
    dispatch('addSyncedPerson', doc);
  } else {
    if (found._rev !== doc._rev) {
      dispatch('syncPersonProperties', { updatePerson: found, newPersonProperties: doc });
    }
  }
}

function syncStage(doc) {
  var found = state.stages[doc._id];
  if (!found && doc.projectId === state.currentProject._id) {
    dispatch('addSyncedStage', doc);
  } else if (found) {
    if (found._rev !== doc._rev) {
      delete doc.showChildren;
      dispatch('syncStageProperties', { updateStage: found, newStageProperties: doc });
    }
  }
}

function syncProject(doc) {
  var project = state.currentProject;
  if (project && project._id === doc._id) {
    if (project._rev !== doc._rev) {
      dispatch('syncProjectProperties', { updateProject: project, newProjectProperties: doc });
    }
  }
  project = state.projectList.find((p) => p._id === doc._id);
  if (!project) {
    dispatch('addSyncedProject', doc);
  } else if (project._id === doc._id) {
    dispatch('syncProjectProperties', { updateProject: project, newProjectProperties: doc });
  }
}

function syncDiary(doc) {
  // Diary Synced from DB
  var diary = state.setDiary;
  if (diary && diary._id === doc._id) {
    if (diary._rev !== doc._rev) {
      dispatch('syncDiaryProperties', { updateDiary: diary, newDiaryProperties: doc });
    }
  }

  if (!state.currentProject.dagbokId === doc._id) {
    dispatch('addSyncedDiary', doc);
  } else if (diary._id === doc._id) {
    dispatch('syncDiaryProperties', { updateDiary: diary, newDiaryProperties: doc });
  }
}



// WEBPACK FOOTER //
// ./src/store/syncer.js